import {html, HTMLTemplateResult} from "lit";
import {customElement} from "lit/decorators/custom-element.js";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {classMap} from "lit/directives/class-map.js";
import {repeat} from "lit/directives/repeat.js";
import {get, translate} from "lit-translate";
import {fromEvent, Subscription} from "rxjs";
import {takeUntil} from "rxjs/operators";
import {container} from "tsyringe";

import {AbstractLitElement} from "../../abstract-lit.element";
import type {AbstractDialogComponent} from "../../ha-dialog/abstract-dialog.component";
import {DialogCommandName} from "../../ha-dialog/dialog-command.enum";
import type {DialogCommand} from "../../ha-dialog/dialog-command.interface";
import type {
    LinkviewCreateDialogResult,
    LinkviewRenameDialogResult,
} from "../../_dialogs/ha-dialog-linkview-create/ha-dialog-linkview-create.component";
import type {NavigationItem} from "../../ha-navigation-item/navigation-item.interface";
import "../../ha-navigation-item/ha-navigation-item.component";
import {DialogHelper} from "../../../helpers/dialog.helper";
import {NavigationHelper} from "../../../helpers/navigation.helper";
import {SlugHelper} from "../../../helpers/slug.helper";
import {UiHelper} from "../../../helpers/ui.helper";
import {GlobalNotificationService} from "../../../services/global-notification.service";
import {HttpError} from "../../../connectivity/http-error";
import {AbstractLinkviewRepository, LINKVIEW_REPOSITORY} from "../../../domain/repositories/abstract-linkview.repository";
import type {Linkview} from "../../../domain/entities/linkview.entity";
import {resolveLinkviewIcon} from "../../../domain/helpers/linkview-icons.helper";
import {isGimmicksBoard} from "../../../domain/gimmicks/gimmicks-board.helper";

/** i18n namespace of the row menu (menu labels, delete confirmation, errors). */
const I18N_ROW_MENU = 'linkview.sidebar.row-menu';

/** The bare Linkview list route — where a deleted, currently routed board sends the user. */
const LINKVIEW_LIST_PATH = '/linkview';

/**
 * Persistent Linkview-mode sidebar content (Anke, 2026-09-04): "Neu" button
 * + the list of named Linkviews — living in `ha-app`'s `<sidebar>` exactly
 * like `<knowledge-sidebar-list>` does for Wissen mode (same spot, same
 * "survives navigation between the bare mode route and a routed detail"
 * reasoning — see that component's own doc comment).
 *
 * Deliberately the SIMPLE half of that pattern: no virtualizer (a list of
 * dashboards is not going to be thousands of rows the way KnowledgeObjects
 * can be — `repeat()` is enough), no Assets-folder view-model, no
 * `EntityLiveRefreshController`/`BackendClient` reference-count lookup (both
 * backend-specific, and Linkview has neither a live-collab signal nor an
 * embeds concept). `AbstractLinkviewRepository` intentionally mirrors
 * `KnowledgeObjectRepository`'s `items$`/create-then-navigate SHAPE closely
 * enough that swapping the local-only repository for a real one later only
 * touches that one file, not this component.
 */
@customElement('linkview-sidebar-list')
export class LinkviewSidebarList extends AbstractLitElement {

    /**
     * The currently ROUTED Linkview's uid (from `ha-app`'s parsed URL), or
     * `null` on the bare `/linkview` list. Drives which row carries the
     * `active` class — mirrors `knowledge-sidebar-list.selectedSid` exactly.
     */
    @property({type: String, attribute: 'selected-sid'})
    public selectedSid: string | null = null;

    @state()
    private _items: Linkview[] = [];

    /** The RxJS subscription currently listening for `dialog.message` on the
     *  (singleton, reused-while-open) `ha-dialog-linkview-create` instance,
     *  if any (review 0025-01/02) — for the create AND the rename flow alike:
     *  both open that ONE dialog element, so both must share this one slot,
     *  otherwise a rename opened over a still-open create dialog would leave
     *  the create listener attached and one Speichern-click would do both.
     *
     *  Unlike `knowledge-sidebar-list`'s equivalent guard — which works because ITS create call is `await`ed,
     *  keeping the dialog open for the guard's whole lifetime —
     *  `DialogHelper.showDialog` here is synchronous and returns
     *  immediately, so a boolean re-entrancy flag would already be reset
     *  before the user could ever click a second time; it protected nothing.
     *  The real bug it was meant to guard against: a fast double-click
     *  reused the same open dialog element and attached a SECOND
     *  `dialog.message` listener that was never removed, so a single
     *  Speichern-click fired both and created two Linkviews. Fixed instead
     *  with `fromEvent(...).pipe(takeUntil(this._disconnected$))` (see
     *  `_onCreateLinkview`) — unsubscribing the previous subscription before
     *  starting a new one caps it at one listener per open, and `takeUntil`
     *  tears it down on disconnect too, with no separate manual
     *  `removeEventListener` bookkeeping needed. */
    private _dialogSubscription: Subscription | null = null;

    /** Re-entrancy guard for the async row-menu handler (see `_onMenuClick`). */
    private _isProcessingMenu = false;

    private readonly _repo: AbstractLinkviewRepository = container.resolve(LINKVIEW_REPOSITORY);

    public override connectedCallback(): void {
        super.connectedCallback();

        // The Gimmicks board is a Linkboard too, but shown on the Gimmicks page only (feature plan 0071).
        // It stays in `items$` itself: its lock state and toolbar read it from there.
        this._repo.items$
            .pipe(takeUntil(this._disconnected$))
            .subscribe((items) => this._items = items.filter((item) => !isGimmicksBoard(item.uid)));
    }

    protected render(): HTMLTemplateResult {
        return html`
            <button type="button"
                    title=${translate('linkview.sidebar.new')}
                    class="linkview-add add-entity buttonform-line"
                    data-label=${translate('linkview.sidebar.new')}
                    @click=${this._onCreateLinkview}>
                <div class="hover-effect"></div>
            </button>
            <nav role="navigation">
                ${repeat(this._items, (item) => item.uid, (item) => this._renderItem(item))}
            </nav>
        `;
    }

    /**
     * A row — built EXACTLY like the Todo-mode sidebar list (Anke, 2026-09-19:
     * "Aufbau identisch zur Todo-Liste"), i.e. like `ha-app`'s `<nav
     * role="navigation">` + `_renderNavigationItem`:
     *
     *   <nav role="navigation">
     *     <div class="linkview-sidebar-list__row">
     *       <a class="active?" href="/linkview/<uid>/<slug>" title="<Titel>" data-icon="<key>">
     *         <ha-navigation-item>
     *           <div class="ha-app__navigation-item__prefix" title="<Titel>"></div>
     *           <div class="ha-app__navigation-item__label"><Titel></div>
     *         </ha-navigation-item>
     *       </a>
     *       <button type="button" class="linkview-sidebar-list__menu" aria-haspopup="menu"
     *               aria-label="Aktionen" title="Aktionen"></button>
     *     </div>
     *   </nav>
     *
     * THE ROW WRAPPER + SIBLING BUTTON (Anke, 2026-09-19: 3-dot menu with
     * "Umbenennen"/"Löschen" like the whiteboard files): an interactive element
     * must not live inside an `<a>`, so the menu button is the `<a>`'s SIBLING
     * inside a flex row. The Todo rule `nav[role="navigation"] > a` only matches
     * a DIRECT child and therefore no longer reaches the `<a>` — linkview_sidebar.css
     * re-states what the row needs (`.linkview-sidebar-list__row > a`), and the
     * button's glyph (`\f142`) is drawn there as well; `data-icon` and `active`
     * stay on the `<a>`, so every icon/active/hover rule below keeps working.
     *
     * The `<ha-navigation-item>` markup is the existing component's (fed a
     * `NavigationItem`), not rebuilt here, so `todo_sidebar.css`'s
     * `sidebar nav[role="navigation"]`, `ha-navigation-item`, `.ha-app__navigation-item__prefix|label` and
     * `a.active …` rules apply unchanged. The routed board gets `active` on
     * the `<a>` (like Todo), not `.selected`.
     *
     * THE ICON lives in the PREFIX, drawn by CSS: the `data-icon="<key>"`
     * attribute sits on the `<a>` (a stable ancestor of the prefix that the
     * component itself does not re-render), and linkview_sidebar.css targets
     * `linkview-sidebar-list a[data-icon="<key>"] .ha-app__navigation-item__prefix::before`
     * — the same `::before`-glyph mechanism the Todo entries use
     * (`a[href="…"] .ha-app__navigation-item__prefix:before`), so size/font/
     * colour/hover/active come from the shared Todo rules. `prefix` is
     * therefore `null` (no second glyph is drawn). The key is passed through
     * `resolveLinkviewIcon`: `null` (board from before the field) or an
     * unknown key renders the default `link`.
     *
     * The lock-toggle and "+ Neue Kachel" row buttons stay gone (2026-09-17,
     * they moved to the toolbar in `o2d-linkview.view.ts`).
     */
    private _renderItem(item: Linkview): HTMLTemplateResult {
        const path = buildLinkviewPath(item);
        const isOpen = item.uid === this.selectedSid;
        const navigationItem: NavigationItem = {label: item.title, prefix: null};
        return html`
            <div class="linkview-sidebar-list__row">
                <a class=${classMap({active: isOpen})}
                   href=${path}
                   title=${item.title}
                   data-icon=${resolveLinkviewIcon(item.icon)}
                   @click=${(e: MouseEvent) => this._onClickItem(e, path)}
                ><ha-navigation-item .item=${navigationItem}></ha-navigation-item></a>
                <button type="button"
                        class="linkview-sidebar-list__menu"
                        aria-haspopup="menu"
                        aria-label=${translate(`${I18N_ROW_MENU}.actions`)}
                        title=${translate(`${I18N_ROW_MENU}.actions`)}
                        @click=${(e: Event) => this._onMenuClick(e, item)}></button>
            </div>
        `;
    }

    private _onClickItem(event: MouseEvent, path: string): void {
        event.preventDefault();
        NavigationHelper.goto(path);
    }

    private _notifyPersistFailed(): void {
        UiHelper.notify({
            level: 'error',
            title: 'Fehler',
            content: html`<p>Die Änderung konnte nicht gespeichert werden.</p>`,
            closable: true,
        });
    }

    /**
     * Opens the create dialog (title + icon). Unlike
     * `knowledge-sidebar-list`'s equivalent (which just opens the dialog and
     * lets IT own post-create navigation, since that dialog persists via a
     * real CommandBus/repository), this listens for the dialog's OKAY itself
     * — `AbstractLinkviewRepository` isn't a `CommandBus`-backed repository the
     * dialog could call directly (see that repository's own doc comment).
     */
    private _onCreateLinkview = (): void => {
        // `{linkview: null}`, never a bare `null`: `DialogHelper.showDialog`
        // returns the ALREADY OPEN dialog of this kind and applies the payload
        // with `Object.assign(dialog, payload)` — and `Object.assign(x, null)`
        // is a no-op. Passing `null` left a dialog that was still open in RENAME
        // mode pointed at the old board (old title prefilled, rename header, no
        // icon field) while this create listener was already attached, so
        // Speichern created a board under the old name and without an icon.
        const dialog = DialogHelper.showDialog('ha-dialog-linkview-create', {linkview: null}) as AbstractDialogComponent | null;
        if (!dialog) {
            return;
        }

        // See `_dialogSubscription`'s own doc comment: unsubscribe
        // whatever a previous click left listening on this (possibly reused)
        // dialog element before starting a new one, so at most one ever
        // fires per Speichern-click.
        this._dialogSubscription?.unsubscribe();
        this._dialogSubscription = fromEvent(dialog, 'dialog.message')
            .pipe(takeUntil(this._disconnected$))
            .subscribe((event) => {
                const detail = (event as CustomEvent<{ value: DialogCommand<LinkviewCreateDialogResult> }>).detail;
                if (detail.value.command !== DialogCommandName.OKAY) {
                    return;
                }
                const {title, icon} = detail.value.payload;
                this._repo.create(title, icon)
                    .then((linkview) => NavigationHelper.goto(buildLinkviewPath(linkview)))
                    .catch(() => this._notifyPersistFailed());
            });
    };

    /**
     * Opens the row's action menu. `stopPropagation`/`preventDefault` keep the
     * click from doing anything else — the button is a sibling of the `<a>`, so
     * nothing would navigate anyway, but the popover must never leak a click to
     * an ancestor handler either.
     *
     * Re-entrancy guard + try/catch as in `knowledge-object-item`: a second
     * click while the menu/confirm is pending would open a second menu and
     * dispatch a second delete, and a rejected delete would otherwise surface
     * as an unhandled rejection with no user feedback. Released in `finally`, so
     * a failure never dead-locks the menu.
     *
     * The ERROR TEXTS are split by what actually failed, the way `_onRename`
     * already splits its own: a menu that never opened is not a delete that
     * failed, and telling the user "Das Linkboard konnte nicht gelöscht werden."
     * for something that was never attempted is simply wrong. Opening the menu
     * gets `error-menu` here; the confirmation gets it inside `_deleteLinkview`;
     * only the delete command itself still reports `error-delete`.
     */
    private async _onMenuClick(event: Event, item: Linkview): Promise<void> {
        event.stopPropagation();
        event.preventDefault();

        if (this._isProcessingMenu) {
            return;
        }
        this._isProcessingMenu = true;

        try {
            const target = (event.currentTarget ?? event.target) as HTMLElement;

            let selected: Awaited<ReturnType<typeof UiHelper.showContextMenu>>;
            try {
                selected = await UiHelper.showContextMenu(target, [
                    {id: 'rename', label: get(`${I18N_ROW_MENU}.rename`), type: 'item'},
                    {id: 'delete', label: get(`${I18N_ROW_MENU}.delete`), type: 'item'},
                ]);
            } catch (error) {
                console.error(`Failed to open the row menu for linkview «${item.uid}».`, error);
                this._notifyFailure('error-menu', error);
                return;
            }

            if (selected?.id === 'rename') {
                this._onRename(item);
                return;
            }

            if (selected?.id !== 'delete') {
                return;
            }

            await this._deleteLinkview(item);
        } finally {
            this._isProcessingMenu = false;
        }
    }

    /**
     * Confirm-gated delete through the repository (which rejects on a refused
     * command, so a failed delete is reported to the user instead of the row
     * silently vanishing — and only THAT failure says `error-delete`; a
     * confirmation that never came up says `error-menu`, see `_onMenuClick`).
     * The list loses the entry through `items$`, which
     * the repository updates itself. If the deleted board is the one currently
     * routed, its detail route no longer exists — go back to the bare list.
     *
     * `selectedSid` is read AFTER the await: the user may have navigated while
     * the confirm dialog or the request was pending, and pulling them away from
     * some other board would be wrong.
     */
    private async _deleteLinkview(item: Linkview): Promise<void> {
        let confirmed: boolean;
        try {
            confirmed = await UiHelper.confirm({
                title: get(`${I18N_ROW_MENU}.confirm-title`),
                // The board's name is an interpolation value, not a concatenation —
                // the placeholder's position differs per language and must stay
                // under the translator's control.
                content: html`<p>${get(`${I18N_ROW_MENU}.confirm-content`, {title: item.title})}</p>`,
                confirmLabel: get(`${I18N_ROW_MENU}.confirm-submit`),
                cancelLabel: get(`${I18N_ROW_MENU}.confirm-cancel`),
            });
        } catch (error) {
            // The question itself never reached the user — nothing was deleted,
            // so this must not read as a failed delete (see `_onMenuClick`).
            console.error(`Failed to ask for delete confirmation of linkview «${item.uid}».`, error);
            this._notifyFailure('error-menu', error);
            return;
        }
        if (!confirmed) {
            return;
        }

        try {
            await this._repo.delete(item.uid);
        } catch (error) {
            console.error(`Failed to delete linkview «${item.uid}».`, error);
            this._notifyFailure('error-delete', error);
            return;
        }

        if (this.selectedSid === item.uid) {
            NavigationHelper.goto(LINKVIEW_LIST_PATH);
        }
    }

    /**
     * Opens the shared dialog in RENAME mode (prefilled title, no icon picker)
     * and waits for its OKAY, exactly like `_onCreateLinkview` does for create —
     * including the shared single-subscription slot (see `_dialogSubscription`).
     * Own try/catch so a synchronous throw from `DialogHelper.showDialog` gets a
     * rename-specific message, not the delete one from `_onMenuClick`.
     */
    private _onRename(item: Linkview): void {
        try {
            const dialog = DialogHelper.showDialog('ha-dialog-linkview-create', {linkview: item}) as AbstractDialogComponent | null;
            if (!dialog) {
                return;
            }

            this._dialogSubscription?.unsubscribe();
            this._dialogSubscription = fromEvent(dialog, 'dialog.message')
                .pipe(takeUntil(this._disconnected$))
                .subscribe((event) => {
                    const detail = (event as CustomEvent<{ value: DialogCommand<LinkviewRenameDialogResult> }>).detail;
                    if (detail.value.command !== DialogCommandName.OKAY) {
                        return;
                    }
                    void this._renameLinkview(item, detail.value.payload.title);
                });
        } catch (error) {
            console.error(`Failed to open rename dialog for linkview «${item.uid}».`, error);
            this._notifyFailure('error-rename', error);
        }
    }

    /**
     * Renames through the repository. The slug in the path derives from the
     * title, so if the renamed board is the one currently routed the address
     * must follow — otherwise a reload would land on the old slug. (The router
     * keys on the uid, so nothing else breaks either way.) Any other board is
     * left alone: renaming must never navigate.
     */
    private async _renameLinkview(item: Linkview, title: string): Promise<void> {
        if (title === item.title) {
            return; // Nothing changed — no command for a no-op.
        }

        try {
            const renamed = await this._repo.rename(item.uid, title);

            if (this.selectedSid === item.uid) {
                NavigationHelper.goto(buildLinkviewPath(renamed));
            }
        } catch (error) {
            console.error(`Failed to rename linkview «${item.uid}».`, error);
            this._notifyFailure('error-rename', error);
        }
    }

    /**
     * Tells the user a row action failed: the translated headline, plus the
     * reason — the "board is locked by someone else" answer (HTTP 409) as its
     * own plain sentence, otherwise whatever message the error carries.
     */
    private _notifyFailure(titleKey: 'error-delete' | 'error-rename' | 'error-menu', error: unknown): void {
        const isLocked = error instanceof HttpError && error.status === 409;
        container.resolve(GlobalNotificationService).error(
            get(`${I18N_ROW_MENU}.${titleKey}`),
            isLocked
                ? get(`${I18N_ROW_MENU}.error-locked`)
                : (error instanceof Error ? error.message : undefined),
        );
    }
}

function buildLinkviewPath(item: Linkview): string {
    const slug = SlugHelper.toKebabCase(item.title);
    return slug ? `/linkview/${item.uid}/${slug}` : `/linkview/${item.uid}`;
}
